import {
  useState,
  MouseEvent,
  ChangeEvent,
  useEffect,
  useCallback
} from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  ListItem,
  List,
  ListItemText,
  Divider,
  Switch,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer
} from '@mui/material';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import CustomButtonOutlined from 'src/components/CustomButton/CustomButtonOutlined';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { format } from 'date-fns';

// Define interfaces for better type safety
interface ActivityLog {
  id: number;
  browser: string | null;
  ipaddress: string;
  email: string;
  date: string;
}

function SecurityTab() {
  const [page, setPage] = useState(0);
  const [listData, setListData] = useState<ActivityLog[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [switchTo, setSwitchTo] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Memoize getData to prevent unnecessary re-renders
  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpRequest(
        'GET',
        `/applications/getActivityLogs?Page=${page + 1}&Size=${rowsPerPage}`
      );

      if (response?.status === 'success' && response.data) {
        const { logs, pagination } = response.data;

        const formattedLogs: ActivityLog[] = logs.map((item) => ({
          id: item.LogID,
          browser: item.BrowserType,
          ipaddress: item.IP,
          email: item.EmailID,
          date: item.EventDate
        }));

        setListData(formattedLogs);
        setTotalCount(pagination.totalRecords);
      } else {
        toast.error(response?.message || 'Failed to fetch activity logs');
      }
    } catch (error) {
      toast.error('An error occurred while fetching activity logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const renderSecuritySettings = () => (
    <>
      <Grid item xs={12}>
        <Box pb={2}>
          <Typography variant="h3">Security</Typography>
          <Typography variant="subtitle2">
            Change your security preferences below
          </Typography>
        </Box>
        <Card>
          <List>
            <ListItem sx={{ p: { xs: 1, sm: 3 } }}>
              <ListItemText
                primaryTypographyProps={{ variant: 'h5', gutterBottom: true }}
                secondaryTypographyProps={{
                  variant: 'subtitle2',
                  lineHeight: 1
                }}
                primary="Change Password"
                secondary="You can change your password here"
              />
              <CustomButtonOutlined
                onClick={() => setSwitchTo('Change Password')}
                title="Change password"
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ p: { xs: 1, sm: 3 } }}>
              <ListItemText
                primaryTypographyProps={{ variant: 'h5', gutterBottom: true }}
                secondaryTypographyProps={{
                  variant: 'subtitle2',
                  lineHeight: 1
                }}
                primary="Two-Factor Authentication"
                secondary="Enable PIN verification for all sign in attempts"
              />
              <Switch color="primary" />
            </ListItem>
          </List>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="Access Logs"
            subheader="Recent sign in activity logs"
          />
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Browser</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Email Id</TableCell>
                  <TableCell>Date/Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listData.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{log.browser || 'Unknown'}</TableCell>
                    <TableCell>{log.ipaddress}</TableCell>
                    <TableCell>{log.email}</TableCell>
                    <TableCell>
                        {format(new Date(log.date), 'MM/dd/yyyy hh:mm:ss a')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box p={2}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              showFirstButton
              showLastButton
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Box>
        </Card>
      </Grid>
    </>
  );

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <Grid container spacing={3}>
      {switchTo !== '' ? (
        <Grid item xs={12}>
          <Box
            pb={2}
            padding={4}
            bgcolor="white"
            borderRadius={1}
            boxShadow={2}
          >
            <EditFormfeatureViews
              switchTo={switchTo}
              setSwitchTo={setSwitchTo}
              applicationDetails={{}}
            />
          </Box>
        </Grid>
      ) : (
        renderSecuritySettings()
      )}
    </Grid>
  );
}

export default SecurityTab;
